// map-view.component.ts
import { HttpClient } from '@angular/common/http';
import { Component, AfterViewInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import * as L from 'leaflet';
import { CurrencyConverterPurePipe } from '../currency-converter-pure.pipe';

type MapMode = 'general' | 'route' | 'transfer' | 'auto';

interface GeocodingResult {
  lat: number;
  lng: number;
  name: string;
  display_name?: string;
}

@Component({
  selector: 'app-map-view',
  templateUrl: './map-view.component.html',
  providers: [CurrencyConverterPurePipe]
})
export class MapViewComponent implements AfterViewInit, OnChanges {
  private map!: L.Map;
  private markers: L.Marker[] = [];
  private polylines: L.Polyline[] = [];
  private pickupMarker?: L.Marker;
  private dropoffMarker?: L.Marker;
  private routeStopOverlays: { stop: any, order: number, el: HTMLDivElement }[] = [];

  @Input() coordinatesArr: any[] = [];
  @Input() currencies: any[] = [];
  @Input() currencyAgency: any;
  @Input() mapMode: MapMode = 'auto'; // 'general', 'route', 'transfer', 'auto'
  @Input() routeColor: string = '#3388ff';
  @Input() lineWeight: number = 2;
  @Input() clientSetting: any;
  @Input() coordinatesSelected: any;
  @Input() initialZoom: number = 13;
  @Input() maxAutoZoom?: number;
  @Input() distanceKm?: number;
  @Input() showPrices: boolean = true;
  @Input() mapHeight: string = "500px";
  /** Índice de la parada seleccionada (para resaltar la card con borde verde) */
  @Input() selectedStopIndex: number = -1;

private isGeocoding = false;

    @Input() pickupData?: {
    lat?: number,
    lng?: number,
    name?: string,
    iata?: string,
    type?: string,
    country_code?: string;
  };

  @Input() dropoffData?: {
    lat?: number,
    lng?: number,
    name?: string,
    iata?: string,
    type?: string,
    country_code?: string;
  };
@Output() selectProductFromMap = new EventEmitter<any>();
/** Emite el stop + índice cuando el usuario hace click en un marcador de ruta */
@Output() stopSelected = new EventEmitter<{ stop: any, index: number }>();

  constructor(private http: HttpClient, private currencyPipe: CurrencyConverterPurePipe,) {}

  ngAfterViewInit(): void {
    setTimeout(() => this.initMap(), 100);
  }

ngOnChanges(changes: SimpleChanges): void {
  // Solo esto agrego - una bandera para evitar múltiples ejecuciones
  if (this.isGeocoding) {
    return;
  }

  // Verificar si coordinatesArr necesita geocodificación (defensive check)
  const coordinatesNeedsGeocoding = changes['coordinatesArr'] &&
    this.coordinatesArr &&
    this.coordinatesArr.length > 0 &&
    this.coordinatesArr.some(item => !this.hasCoordinates(item));

  const needsGeocoding = (changes['pickupData'] && !this.hasCoordinates(this.pickupData)) ||
                        (changes['dropoffData'] && !this.hasCoordinates(this.dropoffData)) ||
                        coordinatesNeedsGeocoding;
  if (needsGeocoding) {
    this.isGeocoding = true;

    this.geocodeLocations().then(() => {
      this.isGeocoding = false;
      if (!this.map) {
        this.initMap(); // Crear el mapa si no existía
      } else {
        this.renderMap();
      }
    });
  } else if (this.map && (changes['coordinatesArr'] || changes['mapMode'] || changes['pickupData'] || changes['dropoffData'])) {
    this.renderMap();
  }

  // Si cambia el stop seleccionado (pero no el array de coordenadas ni el modo),
  // solo actualizamos los bordes de las cards sin re-renderizar todo el mapa.
  if (this.map && changes['selectedStopIndex'] && !changes['coordinatesArr'] && !changes['mapMode']) {
    this.applySelectedStopHighlight();
  }
}

/**
 * Aplica el resaltado (borde verde turquesa) a la card correspondiente
 * al índice seleccionado, sin re-renderizar el mapa entero.
 */
private applySelectedStopHighlight(): void {
  this.routeStopOverlays.forEach((overlay) => {
    const idx = Number(overlay.el.dataset['stopIndex']);
    const isSelected = idx === this.selectedStopIndex;
    overlay.el.style.border = isSelected ? '2px solid rgb(0, 255, 180)' : '1px solid #cccccc';
    overlay.el.style.zIndex = isSelected ? '600' : '500';
  });
}

    private async geocodeLocations(): Promise<void> {
    const geocodingPromises: Promise<void>[] = [];

    // Geocodificar pickup si no tiene coordenadas pero tiene nombre/IATA
    if (this.pickupData && !this.hasCoordinates(this.pickupData)) {
      geocodingPromises.push(
        this.geocodeLocation(this.pickupData).then(coords => {
          if (coords) {
            this.pickupData = { ...this.pickupData, ...coords };
          }
        })
      );
    }
    // Geocodificar dropoff si no tiene coordenadas pero tiene nombre/IATA
    if (this.dropoffData && !this.hasCoordinates(this.dropoffData)) {
      geocodingPromises.push(
        this.geocodeLocation(this.dropoffData).then(coords => {
          if (coords) {
            this.dropoffData = { ...this.dropoffData, ...coords };
          }
        })
      );
    }
    // Geocodificar elementos del array si no tienen coordenadas
    if (this.coordinatesArr) {
      const itemsToGeocode = this.coordinatesArr.filter(item => !this.hasCoordinates(item));

      for (const item of itemsToGeocode) {
        geocodingPromises.push(
          this.geocodeLocation(item).then(coords => {
            if (coords) {
              Object.assign(item, coords);
            }
          })
        );
      }
    }

    await Promise.all(geocodingPromises);
  }

   private async geocodeLocation(location: any): Promise<GeocodingResult | null> {
    // Si ya tiene coordenadas, retornarlas
    if (this.hasCoordinates(location)) {
      return {
        lat: location.lat || parseFloat(location.coordinates?.latitude),
        lng: location.lng || parseFloat(location.coordinates?.longitude),
        name: location.name || location.hotel_name || ''
      };
    }

    // Buscar por IATA (código de aeropuerto)
    if (location.iata) {
      return this.geocodeByIATA(location.iata, location.name);
    }

    // Buscar por nombre
    if (location.name) {
      return this.geocodeByName(location.name, location.type);
    }

    console.warn('No se puede geocodificar: falta nombre o IATA', location);
    return null;
  }

   private getKnownAirports(): Array<{iata: string, lat: number, lng: number, name: string}> {
    return [
      { iata: 'ROS', lat: -32.9036, lng: -60.7850, name: 'Rosario, Islas Malvinas Aeropuerto, Argentina' },
      { iata: 'EZE', lat: -34.8222, lng: -58.5358, name: 'Aeropuerto Internacional Ministro Pistarini, Argentina' },
      { iata: 'AEP', lat: -34.5592, lng: -58.4156, name: 'Aeroparque Jorge Newbery, Argentina' },
      { iata: 'MDZ', lat: -32.8317, lng: -68.7928, name: 'Aeropuerto Internacional Gobernador Francisco Gabrielli, Argentina' },
      { iata: 'COR', lat: -31.3236, lng: -64.2081, name: 'Aeropuerto Internacional Ingeniero Ambrosio Taravella, Argentina' },
      { iata: 'BRC', lat: -41.1511, lng: -71.1575, name: 'Aeropuerto Internacional de San Carlos de Bariloche, Argentina' },
      { iata: 'EPA', lat: -34.6099, lng: -58.6126, name: 'El Palomar Airport, Argentina' },
      // Agrega más aeropuertos según necesites
    ];
  }

private hasCoordinates(data: any): boolean {
  if (!data) return false;

  // Verifica que ambos sean números válidos (no null/undefined/NaN)
  const hasValidLat = data.lat != null &&
                     typeof data.lat === 'number' &&
                     !isNaN(data.lat);

  const hasValidLng = data.lng != null &&
                     typeof data.lng === 'number' &&
                     !isNaN(data.lng);

  return hasValidLat && hasValidLng;
}

  private initMap(): void {
    if (this.map) return; // Evitar crear múltiples mapas

    const hasData = this.coordinatesArr.length > 0 || this.pickupData || this.dropoffData;

    if (!hasData) {
      console.warn('No hay datos para mostrar en el mapa');
      return;
    }

    const center = this.calculateMapCenter();
    this.map = L.map('map').setView(center, this.initialZoom);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(this.map);

    this.renderMap();
  }

  private async geocodeByIATA(iataCode: string, locationName?: string): Promise<GeocodingResult | null> {
    try {
      // Opción 1: Buscar en nuestra base de IATA conocidos
      const knownAirports = this.getKnownAirports();
      const knownAirport = knownAirports.find(a => a.iata === iataCode);

      if (knownAirport) {
        return {
          lat: knownAirport.lat,
          lng: knownAirport.lng,
          name: knownAirport.name
        };
      }

      // Opción 2: Usar Nominatim (OpenStreetMap) para buscar
      const searchQuery = locationName || `${iataCode} airport`;
      return await this.geocodeWithNominatim(searchQuery);

    } catch (error) {
      console.error(`Error geocodificando IATA ${iataCode}:`, error);
      return null;
    }
  }

   private async geocodeByName(name: string, type?: string): Promise<GeocodingResult | null> {
    try {
      let searchQuery = name;

      // Mejorar la búsqueda según el tipo
      if (type === 'airport') {
        searchQuery = `${name} aeropuerto`;
      } else if (type === 'city') {
        searchQuery = `${name} ciudad`;
      } else if (type === 'hotel') {
        searchQuery = `${name} hotel`;
      }

      return await this.geocodeWithNominatim(searchQuery);

    } catch (error) {
      console.error(`Error geocodificando ${name}:`, error);
      return null;
    }
  }



  private async geocodeWithNominatim(query: string): Promise<GeocodingResult | null> {
    try {
      // Nominatim API de OpenStreetMap (gratuita, requiere attribution)
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;

      const response: any = await this.http.get(url).toPromise();

      if (response && response.length > 0) {
        const result = response[0];

        return {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          name: result.name || result.display_name,
          display_name: result.display_name
        };
      }

      return null;
    } catch (error) {
      console.error('Error en geocodificación Nominatim:', error);
      return null;
    }

  }

  private calculateMapCenter(): L.LatLngExpression {
    // Prioridad: pickup -> primer dato -> Buenos Aires por defecto
    if (this.pickupData) {
      return [this.pickupData.lat, this.pickupData.lng];
    }

    if (this.coordinatesArr.length > 0) {
      const first = this.coordinatesArr[0];
      if (first.coordinates) {
        return [parseFloat(first.coordinates.latitude), parseFloat(first.coordinates.longitude)];
      } else if (first.latitude || first.lat) {
        return [parseFloat(first.latitude || first.lat), parseFloat(first.longitude || first.lng)];
      }
    }

    return [-34.6037, -58.3816];
  }

  private renderMap(): void {
    if (!this.map) return;

    this.clearMap();
    const mode = this.determineMode();

    // Si no hay coordenadas aún (todavía en geocodificación), no renderizar
    const hasValidCoords = this.coordinatesArr.some(item => this.isCoordinateItem(item)) ||
                          (this.pickupData && this.hasCoordinates(this.pickupData)) ||
                          (this.dropoffData && this.hasCoordinates(this.dropoffData));

    if (!hasValidCoords) {
      return;
    }

    switch (mode) {
      case 'general':
        this.renderGeneralMode();
        break;
      case 'route':
        this.renderRouteMode();
        break;
      case 'transfer':
        this.renderTransferMode();
        break;
    }

    this.fitMapToContent();
  }

  private determineMode(): MapMode {
    if (this.mapMode !== 'auto') return this.mapMode;

    // Auto-detección basada en datos
    if (this.pickupData && this.dropoffData) return 'transfer';
    if (this.coordinatesArr.length > 0 && this.coordinatesArr[0].order !== undefined) return 'route';
    return 'general';
  }

  // 🎯 MODO 1: GENERAL (Hoteles, Tickets, etc.)
  private renderGeneralMode(): void {

    this.coordinatesArr.forEach((item, index) => {
      if (this.isCoordinateItem(item)) {
        this.addGeneralMarker(item, index);
      }
    });
  }

private addGeneralMarker(item: any, index: number): void {
  const { lat, lng } = this.extractCoordinates(item);
  if (isNaN(lat) || isNaN(lng)) return;

  const name = item.hotel_name || item.name || item.description || `Punto ${index + 1}`;
  const isHotel = !!item.hotel_name;

  const marker = L.marker([lat, lng], {
    title: name,
    icon: this.createHotelIconWithPrice(item, index)
  });

  marker.on('popupopen', () => {
    setTimeout(() => {
      const button = document.getElementById(`hotel-btn-${item.id}`);
      if (button) {
        button.onclick = (event) => {
          event.stopPropagation();
          this.selectProduct(item.id);
          marker.closePopup();
        };
      }
    }, 100);
  });

  const popupContent = this.createHotelPopup(item);

  // 🔥 AQUÍ CONFIGURAS EL ANCHO DEL POPUP
  marker.bindPopup(popupContent, {
    minWidth: 350,
    maxWidth: 350,
    closeButton: false
  });

  marker.addTo(this.map);
  this.markers.push(marker);
}

  private createGeneralIcon(index: number, isHotel: boolean): L.DivIcon {
    const colors = isHotel
      ? ['#FF5252', '#FF4081', '#E040FB', '#7C4DFF', '#536DFE'] // Hoteles
      : ['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800']; // Otros

    return L.divIcon({
      html: `<div style="
        background: ${colors[index % colors.length]};
        color: white;
        border-radius: ${isHotel ? '50%' : '30%'};
        width: ${isHotel ? '35px' : '30px'};
        height: ${isHotel ? '35px' : '30px'};
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: ${isHotel ? '12px' : '10px'};
        border: 2px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      ">${isHotel ? 'H' : 'P'}${index + 1}</div>`,
      className: isHotel ? 'hotel-marker' : 'point-marker',
      iconSize: isHotel ? [35, 35] : [30, 30],
      iconAnchor: isHotel ? [17, 17] : [15, 15]
    });
  }

  // 🔖 ICONO SOLO CON NOMBRE (mismo diseño que el de precio, pero sin precio)
  private createNameOnlyIcon(name: string, index: number): L.DivIcon {
    const mainColor = this.clientSetting?.clients_setting_global?.color_main_1 || '#4CAF50';
    const displayName = name.length > 13 ? name.substring(0, 12) + '…' : name;

    return L.divIcon({
      html: `
        <div style="
          position: relative;
          width: 120px;
          height: 70px;
          cursor: pointer;
        ">
          <!-- Cuerpo principal del marcador (burbuja) -->
          <div style="
            position: absolute;
            bottom: 15px;
            left: 50%;
            transform: translateX(-50%);
            width: auto;
            min-width: 72px;
            max-width: 110px;
            height: 32px;
            background: ${mainColor};
            border-radius: 50rem;
            border: 1.5px solid black;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 10px;
            white-space: nowrap;
            z-index: 10;
          ">
            <span style="
              font-family: 'Rubik', sans-serif;
              color: black;
              font-weight: 600;
              font-size: 11px;
              line-height: 1;
              overflow: hidden;
              text-overflow: ellipsis;
            ">${displayName}</span>
          </div>

          <!-- Contenedor del piquito -->
          <div style="
            position: absolute;
            bottom: 6px;
            left: 50%;
            transform: translateX(-50%);
            width: 20px;
            height: 15px;
            z-index: 99;
          ">
            <!-- Borde negro del piquito -->
            <div style="
              position: absolute;
              bottom: 0;
              left: 50%;
              transform: translateX(-50%);
              width: 0;
              height: 0;
              border-left: 8px solid transparent;
              border-right: 8px solid transparent;
              border-top: 10px solid black;
            "></div>

            <!-- Relleno del piquito (color principal) -->
            <div style="
              position: absolute;
              bottom: 2px;
              left: 50%;
              transform: translateX(-50%);
              width: 0;
              height: 0;
              border-left: 6px solid transparent;
              border-right: 6px solid transparent;
              border-top: 8px solid ${mainColor};
            "></div>
          </div>

          <!-- Pequeño círculo en la punta -->
          <div style="
            position: absolute;
            bottom: -1px;
            left: 50%;
            transform: translateX(-50%);
            width: 6px;
            height: 6px;
            background: black;
            border-radius: 50%;
            border: 1px solid white;
            z-index: 20;
          "></div>
        </div>
      `,
      className: 'name-only-marker',
      iconSize: [120, 70],
      iconAnchor: [60, 68],
      popupAnchor: [0, -50]
    });
  }

// En map-view.component.ts
private createHotelPopup(hotel: any): string {
  // Si showPrices es false, mostrar popup simple sin precios
  if (!this.showPrices) {
    return `
      <div class="d-flex flex-column py-3 px-2" style="min-width: 250px;">
        <div class="d-flex align-items-center">
          <p class="m-0" style="font-family: 'Rubik', sans-serif; font-size: 16px; font-weight: 600; color: black;">${hotel.hotel_name || hotel.name || hotel.description || 'Ubicación'}</p>
        </div>
        ${hotel.address ? `<p class="m-0 mt-1" style="font-family: 'Rubik', sans-serif; font-size: 12px; color: #939393;">${hotel.address}</p>` : ''}
        ${hotel.city ? `<p class="m-0 mt-1" style="font-family: 'Rubik', sans-serif; font-size: 12px; color: #666;">${hotel.city}</p>` : ''}
      </div>
    `;
  }

  // Obtener símbolo de moneda
  const currencySymbol = this.getCurrencySymbol();

  // Formatear precios
  const pricePerNight = hotel.priceUnitary ?
    `${this.currencyAgency?.code} ${this.currencyPipe?.transform(hotel.priceUnitary, hotel.currency, this.currencyAgency.code, this.currencies)?.toFixed(2)}` : 'Consultar';

  const totalPrice = hotel.price ?
  `${this.currencyAgency?.code} ${this.currencyPipe?.transform(hotel.price, hotel.currency, this.currencyAgency.code, this.currencies)?.toFixed(2)}` : 'Consultar';
  // Obtener políticas de cancelación
  const cancellationPolicy = this.formatCancellationPolicy(hotel.cancellationPolicies, hotel.currency);

  const boardName = this.capitalizeFirstWord(hotel.boardName);

  // Obtener imágenes (si existen)
  const mainImage = hotel.images && hotel.images.length > 0 ?
    hotel.images[0] : null;

  // 🔥 CALCULAR ESTRELLAS
  const starRating = hotel.starRating || 0;
  let starsHtml = '';

  for (let i = 0; i < 5; i++) {
    if (i < starRating) {
      starsHtml += '<i class="bi bi-star-fill" style="margin: 2px; color: #fed325;"></i>';
    } else {
      starsHtml += '<i class="bi bi-star-fill" style="margin: 2px; color: #d2d2d2;"></i>';
    }
  }

  return `
    <div class="d-flex flex-column py-3 px-2" style="min-width: 380px;">
      <div class="mb-3">
        <div class="d-flex align-items-center">
          <p class="m-0" style="font-family: 'Rubik', sans-serif; font-size: 19px; font-weight: 400; color: black;line-height: 1">${hotel.hotel_name}</p>
          <div class="d-flex ml-1" style="gap: 1px;margin-top: 2px; visibility: ${hotel.origin === 'HOTELS' ? 'visible' : 'hidden'}" >
            ${starsHtml}
          </div>
        </div>
        <p class="m-0" style="font-family: 'Rubik', sans-serif; font-size: 14px; font-weight: 400; color: #939393;">${hotel.address}</p>
      </div>

      <div class="d-flex align-items-center">
        <div>
          <img style="border-radius: 6px; width: 164px; height: 110px; object-fit: cover;"
               src="${hotel.images?.find((c: any) => c.is_cover === 1)?.thumbnail || '../assets/img/app/hotel_default.png'}">
        </div>
        <div class="d-flex flex-column ml-3">
          <p class="m-0" style="font-family: 'Rubik', sans-serif; font-size: 14px; font-weight: 500; color: black;">${boardName || ''}</p>
          <p class="m-0 mt-2" style="font-family: 'Rubik', sans-serif; font-size: 12px; font-weight: 400; color: ${hotel.rateClass === 'R' ? '#02d498' : '#d60159'};">${hotel.rateClass === 'R' ? 'Reembolsable' : 'No reembolsable'}</p>
          <p class="m-0" style="font-family: 'Rubik', sans-serif; font-size: 11px; font-weight: 400; color: black;">${cancellationPolicy}</p>

          <div class="d-flex align-items-center mt-2">
            <img src="../assets/icons/search-result/view.svg" style="width: 13px; opacity: .7">
            <p class="m-0 ml-1" style="font-family: 'Rubik', sans-serif; font-size: 11px; font-weight: 400; color: #939393;">${hotel.views || 0} views</p>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between mt-4 align-items-center">
        <div class="d-flex flex-column" style="text-align: end; min-width: 164px">
          <p class="m-0" style="font-family: 'Rubik', sans-serif; font-size: 10px; font-weight: 400; color: #666666;line-height: 1;visibility: ${hotel.origin === 'HOTELS' ? 'visible' : 'hidden'}"">Precio por noche ${pricePerNight}</p>
          <p class="m-0" style="font-family: 'Rubik', sans-serif; font-size: 11px; font-weight: 400; color: black;line-height: 1">Precio total ${hotel.origin === 'HOTELS' ? '' : 'por pasajero'}</p>
          <p class="m-0" style="font-family: 'Rubik', sans-serif; font-size: 14px; font-weight: 600; color: black;line-height: 1">${totalPrice}</p>
        </div>

        <div class="pr-4" style="visibility: ${hotel.generalMap ? 'visible' : 'hidden'}">
          <button type="button"
              id="hotel-btn-${hotel.id}"
              style="font-family: 'Rubik', sans-serif; font-size: 12px; font-weight: 600; color: #4a4a4d; border: none; width: 94px; height: 24px; background-color: ${this.clientSetting?.clients_setting_global?.color_main_1 || '#000000'}; cursor: pointer;">
              Agregar
          </button>
        </div>
      </div>
    </div>
  `;
}

// Función para capitalize solo la primera letra de la primera palabra
capitalizeFirstWord(str: string): string {
  if (!str) return '';

  // Convertir todo a minúsculas primero
  const lowerStr = str.toLowerCase();

  // Capitalizar solo la primera letra
  return lowerStr.charAt(0).toUpperCase() + lowerStr.slice(1);
}

selectProduct(productId: number): void {
  this.selectProductFromMap.emit(productId);
}

// 🔧 MÉTODOS AUXILIARES (versión simplificada)
private formatCancellationPolicy(policies: any[], currency: any): string {
  if (!policies || policies.length === 0) return '';

  const policy = policies[0];
  let from = policy.from || policy.dateFrom;
  let text = '';

  const amount = policy.amountProf;

  if (policy.description_policy) {
    text = policy.description_policy;
  } else if (amount && currency && from) {
    const date = new Date(from);
    const formattedDate = date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    });

    text = `Cancelación gratuita hasta el ${formattedDate}. Pasada la fecha se cobrará ${this.currencyAgency?.code} ${this.currencyPipe?.transform(amount, currency, this.currencyAgency.code, this.currencies).toFixed(2)}.`;
  } else if (amount && currency) {
    text = `Cancelación con penalidad: ${this.currencyAgency?.code} ${this.currencyPipe?.transform(amount, currency, this.currencyAgency.code, this.currencies).toFixed(2)}`;
  }

  return text;
}

// 🎨 ACTUALIZAR ICONO CON PRECIO
private createHotelIconWithPrice(hotel: any, index: number): L.DivIcon {
  // Si showPrices es false, usar icono simple con solo el nombre
  if (!this.showPrices) {
    const name = hotel.hotel_name || hotel.name || hotel.description || `P${index + 1}`;
    return this.createNameOnlyIcon(name, index);
  }

  const price = this.currencyPipe?.transform(hotel.price, hotel.currency, this.currencyAgency.code, this.currencies).toFixed(2) || 0;
  let displayPrice = price;
  const mainColor = this.clientSetting?.clients_setting_global?.color_main_1 || '#4CAF50';

  return L.divIcon({
    html: `
      <div style="
        position: relative;
        width: 120px;
        height: 70px; /* Un poco más alto para el piquito */
        cursor: pointer;
      ">
        <!-- Cuerpo principal del marcador (burbuja) -->
        <div style="
          position: absolute;
          bottom: 15px; /* Más arriba para dar espacio al piquito */
          left: 50%;
          transform: translateX(-50%);
          width: auto;
          min-width: 72px;
          height: 32px;
          background: ${mainColor};
          border-radius: 50rem;
          border: 1.5px solid black;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 8px;+
          white-space: nowrap;
          z-index: 10;
        ">
          <!-- Contenido -->
          <div style="display: flex; flex-direction: column; align-items: center; gap: 2px;">
            <span style="
              font-family: 'Rubik', sans-serif;
              color: black;
              font-weight: 600;
              font-size: ${displayPrice > 9999 ? '9px' : '11px'};
              line-height: 1;
            ">${this.currencyAgency?.code}</span>
            <span style="
              font-family: 'Rubik', sans-serif;
              color: black;
              font-weight: 600;
              font-size: ${displayPrice > 9999 ? '9px' : '11px'};
              line-height: 1;
            ">${displayPrice}</span>
          </div>
        </div>

        <!-- Contenedor del piquito (para mejor posicionamiento) -->
        <div style="
          position: absolute;
          bottom: 6px;
          left: 50%;
          transform: translateX(-50%);
          width: 20px;
          height: 15px;
          z-index: 99 ;
        ">
          <!-- Borde negro del piquito (más grande) -->
          <div style="
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 10px solid black;
          "></div>

          <!-- Relleno del piquito (color principal) -->
          <div style="
            position: absolute;
            bottom: 2px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 8px solid ${mainColor};
          "></div>
        </div>

        <!-- Pequeño círculo en la punta (precisión) -->
        <div style="
          position: absolute;
          bottom: -1px;
          left: 50%;
          transform: translateX(-50%);
          width: 6px;
          height: 6px;
          background: black;
          border-radius: 50%;
          border: 1px solid white;
          z-index: 20;
        "></div>
      </div>
    `,
    className: 'hotel-price-marker',
    iconSize: [120, 70],
    iconAnchor: [60, 68], // Anclaje en la punta (casi abajo del todo)
    popupAnchor: [0, -50]
  });
}

  // 🚗 MODO 2: RUTA (Experiencias)
  private renderRouteMode(): void {

    if (this.coordinatesArr.length < 2) return;

    const routePoints: L.LatLngExpression[] = [];
    const sortedStops = [...this.coordinatesArr].sort((a, b) => (a.order || 0) - (b.order || 0));

    // Limpiar overlays previos antes de crear nuevos
    this.removeRouteStopOverlays();

    const total = sortedStops.length;

    sortedStops.forEach((stop, index) => {
      const { lat, lng } = this.extractCoordinates(stop);
      if (isNaN(lat) || isNaN(lng)) return;

      const order = stop.order || index + 1;
      this.addRouteStopMarker(stop, order, index);
      this.addRouteStopCard(stop, order, index, total);
      routePoints.push([lat, lng]);
    });

    if (routePoints.length >= 2) {
      const polyline = L.polyline(routePoints, {
        color: '#000000',
        weight: this.lineWeight,
        opacity: 0.9
      }).addTo(this.map);
      this.polylines.push(polyline);
    }

    // Posicionar cards ahora y cada vez que el mapa se mueva
    this.updateRouteStopOverlaysPosition();
    this.map.on('move zoom moveend zoomend resize', this.updateRouteStopOverlaysPosition);
  }

  private addRouteStopMarker(stop: any, order: number, index: number): void {
    const { lat, lng } = this.extractCoordinates(stop);
    if (isNaN(lat) || isNaN(lng)) return;

    const name = stop.name || stop.description || `Parada ${order}`;

    const marker = L.marker([lat, lng], {
      title: name,
      icon: this.createRouteStopIcon(order, name)
    });

    marker.on('click', () => {
      this.stopSelected.emit({ stop, index });
    });

    marker.addTo(this.map);
    this.markers.push(marker);
  }

  /**
   * Crea la card HTML overlay asociada a una parada de la ruta.
   * La card se monta en un contenedor fijo posicionado sobre el mapa
   * y se reposiciona en cada move/zoom.
   */
  private addRouteStopCard(stop: any, order: number, index: number, total: number): void {
    const mapContainer = this.map.getContainer();
    const overlayHost = this.getOverlayHost();

    const name = stop.name || stop.description || `Parada ${order}`;
    const isSelected = index === this.selectedStopIndex;

    const card = document.createElement('div');
    card.className = isSelected ? 'route-stop-card route-stop-card--selected' : 'route-stop-card';
    card.style.cssText = `
      position: absolute;
      pointer-events: none;
      min-width: 160px;
      max-width: 220px;
      padding: 8px 12px;
      background: #ffffff;
      border: ${isSelected ? '2px solid rgb(0, 255, 180)' : '1px solid #cccccc'};
      border-radius: 6px;
      font-family: 'Rubik', sans-serif;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      transform: translate(-50%, -50%);
      z-index: ${isSelected ? 600 : 500};
      white-space: normal;
    `;

    card.innerHTML = `
      <div style="display: flex; flex-direction: column; min-width: 0;">
        <div style="color: #000000; font-family: 'Rubik' !important; font-size: 13px; font-weight: 600 !important; line-height: 1.3; overflow: hidden; text-overflow: ellipsis;">${name}</div>
        <div style="font-family: 'Rubik'; font-size: 12px; line-height: 1.3;">Hora: 8:40 hs. - 9:05 hs.</div>
      </div>
    `;

    card.dataset['side'] = this.getCardSide(index, total);
    card.dataset['stopIndex'] = String(index);
    overlayHost.appendChild(card);

    this.routeStopOverlays.push({ stop, order, el: card });
  }

  /**
   * Devuelve 'right' o 'left' según el índice y la cantidad total de paradas.
   * Reglas:
   *  - Si solo hay 2 paradas, la primera va a la derecha y la segunda a la izquierda.
   *  - Si hay 3 o más, alterna empezando por la derecha.
   */
  private getCardSide(index: number, total: number): 'right' | 'left' {
    if (total === 1) return 'right';
    if (total === 2) return index === 0 ? 'right' : 'left';
    return index % 2 === 0 ? 'right' : 'left';
  }

  /**
   * Crea (si no existe) un contenedor absoluto sobre el mapa
   * donde se montan las cards de las paradas.
   */
  private getOverlayHost(): HTMLDivElement {
    const mapContainer = this.map.getContainer();
    let host = mapContainer.querySelector<HTMLDivElement>('.route-cards-host');

    if (!host) {
      host = document.createElement('div');
      host.className = 'route-cards-host';
      host.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 500;
        overflow: hidden;
      `;
      mapContainer.appendChild(host);
    }

    return host;
  }

  /**
   * Reposiciona cada card overlay en función de la posición actual
   * del marcador correspondiente en pantalla.
   */
  private updateRouteStopOverlaysPosition = (): void => {
    if (!this.map || this.routeStopOverlays.length === 0) return;

    const container = this.map.getContainer();
    const containerWidth = container.clientWidth;

    // Calcular tamaño una vez para decidir lado según overflow
    this.routeStopOverlays.forEach((overlay) => {
      // Asegurar visibilidad para medir (si estaba oculta por overflow previo)
      overlay.el.style.visibility = 'hidden';
      overlay.el.style.display = 'block';
    });

    this.routeStopOverlays.forEach((overlay, index) => {
      const marker = this.markers[index];
      if (!marker) return;

      const point = this.map.latLngToContainerPoint(marker.getLatLng());
      const side = overlay.el.dataset['side'] === 'left' ? 'left' : 'right';
      const elWidth = overlay.el.offsetWidth || 200;
      const elHeight = overlay.el.offsetHeight || 50;

      // Distancia horizontal desde el centro del marcador a la card
      const offsetX = 30; // px
      let left: number;
      if (side === 'right') {
        left = point.x + offsetX + elWidth / 2;
      } else {
        left = point.x - offsetX - elWidth / 2;
      }

      // Si la card se sale del contenedor por el lado asignado,
      // la mandamos al otro lado
      const halfWidth = elWidth / 2;
      if (left - halfWidth < 4) {
        left = point.x + offsetX + elWidth / 2;
        overlay.el.dataset['side'] = 'right';
      } else if (left + halfWidth > containerWidth - 4) {
        left = point.x - offsetX - elWidth / 2;
        overlay.el.dataset['side'] = 'left';
      }

      const top = point.y;

      overlay.el.style.left = `${left}px`;
      overlay.el.style.top = `${top}px`;
      overlay.el.style.transform = 'translate(-50%, -50%)';
      overlay.el.style.visibility = 'visible';
    });
  };

  /**
   * Limpia los overlays de cards y desuscribe del mapa.
   */
  private removeRouteStopOverlays(): void {
    if (this.map) {
      this.map.off('move zoom moveend zoomend resize', this.updateRouteStopOverlaysPosition);
    }
    this.routeStopOverlays.forEach(o => o.el.remove());
    this.routeStopOverlays = [];
  }

    private createRouteStopIcon(order: number, name?: string): L.DivIcon {
      const label = String(order);
      return L.divIcon({
        html: `<div style="
          position: relative;
          width: 44px;
          height: 44px;
          cursor: pointer;
        ">
          <!-- Círculo verde con borde negro, número en negro -->
          <div style="
            position: absolute;
            top: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 36px;
            height: 36px;
            background: #000000;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
            box-shadow: 0 1px 4px rgba(0,0,0,0.4);
          ">
            <span style="
              font-family: 'Rubik', sans-serif;
              color: #FFFFFF;
              font-weight: 500;
              font-size: 15px;
              line-height: 1;
            ">${label}</span>
          </div>
        </div>
      `,
        className: 'route-stop-marker',
        iconSize: [44, 44],
        iconAnchor: [22, 22],
        popupAnchor: [0, -40]
    });
    }

  // 📍 MODO 3: TRANSFER (Punto A - Punto B)
  private renderTransferMode(): void {

    // Marcar pickup si existe
    if (this.pickupData && this.hasCoordinates(this.pickupData)) {
      const lat = this.pickupData.lat ;
      const lng = this.pickupData.lng ;
      const name = this.pickupData.name || 'Pickup';
      const iata = this.pickupData.iata ? ` (${this.pickupData.iata})` : '';

      this.pickupMarker = L.marker([lat, lng], {
        title: name,
        icon: this.createTransferIcon('pickup', this.pickupData.type)
      }).addTo(this.map);

      this.pickupMarker.bindPopup(`
        <div style="min-width: 200px; padding: 10px;">
          <h4 style="margin: 0 0 8px 0; color: #2c3e50;">
            📍 ${name}${iata}
          </h4>
          <div style="color: #7f8c8d; font-size: 12px;">
            ${this.pickupData.type ? `<div>Tipo: ${this.pickupData.type}</div>` : ''}
            <div>Coordenadas: ${lat.toFixed(6)}, ${lng.toFixed(6)}</div>
            ${this.pickupData.country_code ? `<div>País: ${this.pickupData.country_code}</div>` : ''}
          </div>
        </div>
      `);
    }

    // Marcar dropoff si existe
    if (this.dropoffData && this.hasCoordinates(this.dropoffData)) {
      const lat = this.dropoffData.lat;
      const lng = this.dropoffData.lng;
      const name = this.dropoffData.name || 'Dropoff';
      const iata = this.dropoffData.iata ? ` (${this.dropoffData.iata})` : '';

      this.dropoffMarker = L.marker([lat, lng], {
        title: name,
        icon: this.createTransferIcon('dropoff', this.dropoffData.type)
      }).addTo(this.map);

      this.dropoffMarker.bindPopup(`
        <div style="min-width: 200px; padding: 10px;">
          <h4 style="margin: 0 0 8px 0; color: #2c3e50;">
            🏁 ${name}${iata}
          </h4>
          <div style="color: #7f8c8d; font-size: 12px;">
            ${this.dropoffData.type ? `<div>Tipo: ${this.dropoffData.type}</div>` : ''}
            <div>Coordenadas: ${lat.toFixed(6)}, ${lng.toFixed(6)}</div>
            ${this.dropoffData.country_code ? `<div>País: ${this.dropoffData.country_code}</div>` : ''}
          </div>
        </div>
      `);
    }

    // Dibujar línea entre puntos si ambos existen
    if (this.pickupMarker && this.dropoffMarker) {
      const pickupLatLng = this.pickupMarker.getLatLng();
      const dropoffLatLng = this.dropoffMarker.getLatLng();

      const polyline = L.polyline([
        [pickupLatLng.lat, pickupLatLng.lng],
        [dropoffLatLng.lat, dropoffLatLng.lng]
      ], {
        color: 'rgb(0, 255, 180)',
        weight: 3,
        opacity: 0.6,
      }).addTo(this.map);

      this.polylines.push(polyline);
    }
  }


  private createTransferIcon(type: 'pickup' | 'dropoff', locationType?: string): L.DivIcon {
    let symbol = '📍';
    let color = '#3388ff';

    if (type === 'dropoff') {
      symbol = '🏁';
      color = '#FF5252';
    }

    // Personalizar según tipo de ubicación
    if (locationType === 'airport') {
      symbol = '✈️';
      color = type === 'pickup' ? '#4A90E2' : '#E24A4A';
    } else if (locationType === 'hotel') {
      symbol = '🏨';
      color = type === 'pickup' ? '#4CAF50' : '#FF9800';
    } else if (locationType === 'city') {
      symbol = '🏙️';
      color = type === 'pickup' ? '#9C27B0' : '#FF5722';
    }

    return L.divIcon({
      html: `<div style="
        background: ${color};
        color: white;
        border-radius: 50%;
        width: 42px;
        height: 42px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        border: 3px solid white;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
      ">${symbol}</div>`,
      className: `${type}-marker ${locationType || ''}`,
      iconSize: [42, 42],
      iconAnchor: [21, 21]
    });
  }

  // 🔧 MÉTODOS AUXILIARES
  private extractCoordinates(item: any): { lat: number, lng: number } {
    if (item.coordinates) {
      return {
        lat: parseFloat(item.coordinates.latitude),
        lng: parseFloat(item.coordinates.longitude)
      };
    }

    return {
      lat: parseFloat(item.latitude || item.lat),
      lng: parseFloat(item.longitude || item.lng)
    };
  }

  private isCoordinateItem(item: any): boolean {
    return (item.coordinates && item.coordinates.latitude !== undefined) ||
           (item.latitude !== undefined) ||
           (item.lat !== undefined);
  }

  private getCurrencySymbol(): string {
    if (!this.currencyAgency || !this.currencies) return '$';
    const currency = this.currencies.find(c => c.id === this.currencyAgency);
    return currency?.symbol || '$';
  }

  private clearMap(): void {
    this.markers.forEach(marker => this.map?.removeLayer(marker));
    this.markers = [];

    this.polylines.forEach(polyline => this.map?.removeLayer(polyline));
    this.polylines = [];

    if (this.pickupMarker) this.map?.removeLayer(this.pickupMarker);
    if (this.dropoffMarker) this.map?.removeLayer(this.dropoffMarker);

    this.pickupMarker = undefined;
    this.dropoffMarker = undefined;

    // Limpiar overlays HTML de cards de ruta
    this.removeRouteStopOverlays();
    const host = this.map?.getContainer()?.querySelector('.route-cards-host');
    if (host) host.remove();
  }

  private fitMapToContent(): void {
    const allPoints: L.LatLng[] = [];

    // Recolectar puntos de marcadores
    this.markers.forEach(marker => allPoints.push(marker.getLatLng()));
    if (this.pickupMarker) allPoints.push(this.pickupMarker.getLatLng());
    if (this.dropoffMarker) allPoints.push(this.dropoffMarker.getLatLng());

    // Recolectar puntos de polylines
    this.polylines.forEach(polyline => {
      const latlngs: any = polyline.getLatLngs();
      if (Array.isArray(latlngs)) {
        latlngs.forEach((point: L.LatLng) => allPoints.push(point));
      }
    });

    if (allPoints.length === 0) return;

    const bounds = L.latLngBounds(allPoints);

    // Calcular el zoom dinámico basado en la distancia entre puntos
    let calculatedMaxZoom = this.maxAutoZoom || 15;

    if (this.distanceKm !== undefined && this.distanceKm > 0) {
      // Fórmula empírica: ajustar zoom según distancia
      // Distancias grandes (miles de km) → zoom bajo (2-4)
      // Distancias medias (cientos de km) → zoom medio (5-7)
      // Distancias pequeñas (decenas de km) → zoom alto (8-10)
      if (this.distanceKm > 5000) {
        calculatedMaxZoom = 3 + 4;
      } else if (this.distanceKm > 1000) {
        calculatedMaxZoom = 4 + 4;
      } else if (this.distanceKm > 500) {
        calculatedMaxZoom = 5 + 4;
      } else if (this.distanceKm > 200) {
        calculatedMaxZoom = 6 + 4;
      } else if (this.distanceKm > 50) {
        calculatedMaxZoom = 7 + 4;
      } else if (this.distanceKm > 10) {
        calculatedMaxZoom = 8 + 4;
      } else if (this.distanceKm > 1) {
        calculatedMaxZoom = 9 + 4;
      } else {
        calculatedMaxZoom = 10 + 4;
      }
    } else if (allPoints.length >= 2) {
      // Calcular distancia real entre los puntos más lejanos
      const maxDist = this.calculateMaxDistance(allPoints);
      if (maxDist > 0) {
        this.distanceKm = maxDist;
        if (maxDist > 5000) {
          calculatedMaxZoom = 3 + 4;
        } else if (maxDist > 1000) {
          calculatedMaxZoom = 4 + 4;
        } else if (maxDist > 500) {
          calculatedMaxZoom = 5 + 4;
        } else if (maxDist > 200) {
          calculatedMaxZoom = 6 + 4;
        } else if (maxDist > 50) {
          calculatedMaxZoom = 7 + 4;
        } else if (maxDist > 10) {
          calculatedMaxZoom = 8 + 4;
        } else if (maxDist > 1) {
          calculatedMaxZoom = 9 + 4;
        } else {
          calculatedMaxZoom = 10 + 4;
        }
      }
    }

    this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: calculatedMaxZoom });
  }

  private calculateMaxDistance(points: L.LatLng[]): number {
    let maxDist = 0;
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const dist = this.haversineDistance(points[i].lat, points[i].lng, points[j].lat, points[j].lng);
        if (dist > maxDist) {
          maxDist = dist;
        }
      }
    }
    return maxDist;
  }

  private haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Radio de la Tierra en km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  // Añade estos métodos a tu clase
getModeClass(): string {
  const mode = this.determineMode();
  return `mode-${mode}`;
}

getModeText(): string {
  const mode = this.determineMode();
  const texts = {
    general: '📍 Puntos',
    route: '🗺️ Ruta',
    transfer: '🚗 Traslado'
  };
  return texts[mode] || 'Mapa';
}

getStatsText(): string {
  const mode = this.determineMode();

  switch (mode) {
    case 'general':
      return `${this.coordinatesArr.length} punto${this.coordinatesArr.length !== 1 ? 's' : ''}`;
    case 'route':
      return `${this.coordinatesArr.length} parada${this.coordinatesArr.length !== 1 ? 's' : ''}`;
    case 'transfer':
      const parts = [];
      if (this.pickupData) parts.push('Pickup');
      if (this.dropoffData) parts.push('Dropoff');
      return parts.join(' → ');
    default:
      return '';
  }
}

// Método público para cambiar el modo manualmente
setMode(mode: MapMode): void {
  this.mapMode = mode;
  if (this.map) {
    this.renderMap();
  }
}
}
